import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components/ui/card';
import { useAppContext, Project, LaborItem } from '@/store/AppContext';
import { CheckCircle2, Clock, AlertCircle, Calendar, Filter, Search, ChevronRight } from 'lucide-react';
import { Input } from '@/ui/components/ui/input';
import { Button } from '@/ui/components/ui/button';

export function Tasks() {
  const { projects } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('pending');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');

  const allTasks = projects.flatMap(project => 
    (project.laborItems || []).map(task => ({
      ...task,
      projectName: project.name,
      projectId: project.id
    }))
  );

  const filteredTasks = allTasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         task.projectName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'completed' ? task.isCompleted : !task.isCompleted);
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });
  const hasAnyTasks = allTasks.length > 0;

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-100';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-100';
      case 'low': return 'text-emerald-600 bg-emerald-50 border-emerald-100';
      default: return 'text-slate-600 bg-slate-50 border-slate-100';
    }
  };

  const getPriorityLabel = (priority?: string) => {
    switch (priority) {
      case 'high': return 'Wysoki';
      case 'medium': return 'Średni';
      case 'low': return 'Niski';
      default: return 'Brak';
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto bg-[#0f0f0f] min-h-screen text-white">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Zarządzanie Zadaniami</h1>
          <p className="text-slate-300 mt-1">Przeglądaj i organizuj wszystkie prace na swoich budowach.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Szukaj zadania lub projektu..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select 
              className="w-full pl-10 pr-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">Wszystkie statusy</option>
              <option value="pending">W trakcie</option>
              <option value="completed">Ukończone</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <AlertCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select 
              className="w-full pl-10 pr-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as any)}
            >
              <option value="all">Wszystkie priorytety</option>
              <option value="high">Wysoki</option>
              <option value="medium">Średni</option>
              <option value="low">Niski</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filteredTasks.length > 0 ? (
          filteredTasks.map((task) => (
            <Card key={task.id} className={`hover:shadow-md transition-shadow border-[#2a2a2a] bg-[#1a1a1a] ${task.isCompleted ? 'opacity-75' : ''}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${task.isCompleted ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    {task.isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
                  </div>
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold text-white truncate ${task.isCompleted ? 'line-through text-slate-400' : ''}`}>
                      {task.name}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getPriorityColor(task.priority)}`}>
                      {getPriorityLabel(task.priority)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-300">
                    <span className="flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" /> {task.projectName}
                    </span>
                    {task.deadline && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {task.deadline}
                      </span>
                    )}
                    <span>{task.quantity} {task.unit}</span>
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-sm font-bold text-white">{((task.quantity || 0) * (task.unitPrice || 0)).toFixed(2)} PLN</p>
                  <p className="text-[10px] text-slate-300">Wartość zadania</p>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12 bg-[#1a1a1a] rounded-2xl border border-dashed border-[#2a2a2a]">
            <div className="w-16 h-16 bg-[#0d0d0d] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-medium text-white">{hasAnyTasks ? 'Nie znaleziono zadań' : 'Brak danych'}</h3>
            <p className="text-slate-300">
              {hasAnyTasks ? 'Spróbuj zmienić filtry lub wyszukiwaną frazę.' : 'Zadania pojawią się po zapisaniu wyceny.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
